// Secrets_Channel layer — outer AES-256-GCM wrapping of an E2E
// envelope, keyed by self-ECDH on the user's identity keypair.
//
// This module implements the construction described in `design.md`
// ("Crypto_Layer / packages/crypto/src/secrets.ts") and tracked by
// task 3.9, validating Requirement 10.3:
//
//   SK_msg = HKDF-SHA256(
//     ikm    = ECDH(IK_priv, IK_pub),                  // self-ECDH
//     salt   = random 32 bytes per message,
//     info   = "link.secrets.v1" || channel_id || message_id,
//     length = 32
//   )
//   outer_ct = AES-256-GCM(SK_msg, e2e_envelope_bytes)
//
// Why a *second* AES-GCM layer on top of the standard E2E envelope?
//
//   * Personal_Workspace and DM messages are already E2E-encrypted to
//     the recipient's `IK_pub`. For Secrets_Channels (Personal_Workspace
//     or DM only — see Requirement 10.2), we add a second symmetric
//     layer keyed by self-ECDH on the writer's *own* identity. The
//     intent is defense-in-depth: even if a future bug ever caused the
//     E2E envelope ciphertext to leak in a context where the recipient
//     hasn't yet authenticated (e.g. a stale signed URL, a backup that
//     was supposed to be filtered), the Secrets layer still has to be
//     stripped first by code that proves possession of `IK_priv`.
//   * Self-ECDH = `ECDH(IK_priv, IK_pub)`. ECDH-P256 with the same
//     scalar on both sides reduces to `scalar · scalar · G`, which is
//     a deterministic, single-fixed-curve point: a stable per-Account
//     secret. Mixing it with a 32-byte per-message random salt and a
//     `(channel_id, message_id)`-bound `info` gives a fresh AES-256
//     key per message. The salt is shipped alongside the ciphertext.
//
// Wire format choice
// ------------------
// Like the E2E envelope, the Secrets envelope is canonical JSON with
// unpadded base64url byte fields. The byte layout is far simpler than
// the E2E envelope (no per-recipient slots), so the JSON is fixed at
// `{ v, alg, salt, iv, outerCt }` in a fixed key order. The same
// envelope bytes round-trip byte-for-byte through
// `serializeSecretsEnvelope` / `parseSecretsEnvelope`.
//
// The encoded inner E2E envelope bytes are *not* placed in a
// dedicated field — they ARE `outerCt`'s plaintext. The caller must
// run `parseE2eEnvelope` on the result of `unwrapSecrets` before
// handing it to `decryptE2e`. This matches `messages_e2e.envelope`
// always being a single opaque blob whose `envelope_kind` tag tells
// the consumer which parser to invoke.
//
// Domain-separation guarantees
// ----------------------------
//   * The HKDF `info` always begins with `link.secrets.v1` and
//     concatenates the UTF-8 channel/message IDs. Replaying the
//     ciphertext into a different `(channelId, messageId)` pair makes
//     `unwrapSecrets` derive a different SK_msg and the AES-GCM tag
//     check fails.
//   * The HKDF salt is 32 fresh CSPRNG bytes per call, ensuring SK_msg
//     is fresh even if a (channelId, messageId) is somehow reused.
//   * The E2E envelope's own AAD is unaffected by this layer — once
//     `unwrapSecrets` returns the inner bytes, the standard E2E
//     decrypt still requires the right channel/message context.
//
// _Requirements: 10.3_

import {
  parseE2eEnvelope,
  serializeE2eEnvelope,
  type E2eEnvelope,
} from "../e2e";
import { getSubtle } from "../e2e/internals";

// ---------------------------------------------------------------------------
// Wire constants
// ---------------------------------------------------------------------------

/** Envelope schema version emitted by `wrapSecrets`. */
export const SECRETS_VERSION = 1 as const;

/** Algorithm tag emitted by `wrapSecrets`. */
export const SECRETS_ALG = "self-ecdh-p256+hkdf+aes-256-gcm" as const;

/** Bytes in the per-message HKDF salt. */
export const SECRETS_SALT_BYTES = 32;

/** Bytes in the AES-GCM nonce used for the outer wrap. */
export const SECRETS_IV_BYTES = 12;

/** Bytes in an ECDH-P256 shared secret (the X coordinate). */
const ECDH_SHARED_SECRET_BYTES = 32;

/** Bytes in `SK_msg` — AES-256 raw key. */
const SK_BYTES = 32;

/**
 * HKDF info prefix bound into SK_msg derivation. The full info bytes
 * also include the UTF-8 channel and message IDs (see
 * {@link buildSecretsHkdfInfo}). The `.v1` segment is part of the
 * domain separation: a future v2 envelope cannot collide with v1
 * ciphertext.
 */
const SECRETS_INFO_PREFIX = new TextEncoder().encode("link.secrets.v1");

const TEXT_ENCODER = new TextEncoder();

/** Fixed key order for top-level envelope JSON emission. */
const ENVELOPE_KEY_ORDER = ["v", "alg", "salt", "iv", "outerCt"] as const;

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * The decoded Secrets envelope. {@link wrapSecrets} returns one of
 * these and {@link unwrapSecrets} consumes one. Use
 * {@link serializeSecretsEnvelope} and {@link parseSecretsEnvelope}
 * to convert to/from wire bytes.
 *
 * All byte fields are owned by the envelope — callers MAY mutate them
 * after construction at their own risk; the library does not retain
 * references after the function call returns.
 */
export interface SecretsEnvelope {
  /** Envelope schema version. v1 is the only value currently emitted. */
  v: typeof SECRETS_VERSION;
  /** Algorithm tag. v1 is `self-ecdh-p256+hkdf+aes-256-gcm`. */
  alg: typeof SECRETS_ALG;
  /** Per-message HKDF salt; exactly 32 fresh CSPRNG bytes per wrap. */
  salt: Uint8Array;
  /** 12-byte AES-GCM nonce used for the outer wrap. */
  iv: Uint8Array;
  /**
   * AES-256-GCM ciphertext + 16-byte tag over the serialized inner
   * E2E envelope bytes. SK_msg keys the AES-GCM call, AAD is
   * intentionally absent (the inner envelope's AAD already binds the
   * payload to a `(channelId, messageId)` pair).
   */
  outerCt: Uint8Array;
}

/**
 * Inputs to {@link wrapSecrets}. The caller passes both halves of the
 * identity keypair; `wrapSecrets` performs ECDH internally.
 *
 * `inner` may be supplied as either a decoded {@link E2eEnvelope}
 * (which is then serialized with {@link serializeE2eEnvelope}) or as
 * already-serialized bytes (for callers that already have them on
 * hand and want to avoid a re-encode).
 */
export interface WrapSecretsInput {
  /**
   * Inner E2E envelope to wrap. Either the decoded form (we
   * serialize it via the canonical helper) or the raw serialized
   * bytes from a prior call to {@link serializeE2eEnvelope}.
   */
  inner: E2eEnvelope | Uint8Array;
  /** Channel ID — bound into the HKDF info. */
  channelId: string;
  /** Message ID — bound into the HKDF info. */
  messageId: string;
  /**
   * The user's ECDH-importable private key. Identity keys land in
   * `Key_Store` tagged for ECDSA; the Secrets layer needs an ECDH
   * key over the same scalar. The web client typically re-imports
   * the JWK as ECDH at the point of call (see
   * `apps/web/src/secrets/*` in the integration task) and passes the
   * resulting `CryptoKey` here.
   */
  identityPrivateKey: CryptoKey;
  /**
   * The user's ECDH-importable public key, matching
   * {@link WrapSecretsInput.identityPrivateKey}. Same scalar/curve
   * point as the identity public key in `accounts.public_key`.
   */
  identityPublicKey: CryptoKey;
}

/** Inputs to {@link unwrapSecrets}. */
export interface UnwrapSecretsInput {
  /** Decoded outer envelope. Use {@link parseSecretsEnvelope} on raw bytes. */
  outer: SecretsEnvelope;
  /** Channel ID — must match the value passed at wrap time. */
  channelId: string;
  /** Message ID — must match the value passed at wrap time. */
  messageId: string;
  /** Same as {@link WrapSecretsInput.identityPrivateKey}. */
  identityPrivateKey: CryptoKey;
  /** Same as {@link WrapSecretsInput.identityPublicKey}. */
  identityPublicKey: CryptoKey;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Discriminated kinds of Secrets-layer failure. Mirrors the structure
 * of `E2eErrorKind` so consumers can pattern-match uniformly.
 *
 * - `bad_json`         : input bytes are not valid JSON.
 * - `bad_shape`        : JSON parsed but did not match the schema.
 * - `bad_version`      : `v` is present but not 1.
 * - `bad_alg`          : `alg` is present but not the v1 alg tag.
 * - `bad_base64`       : a byte field could not be base64url-decoded.
 * - `bad_field_length` : a fixed-length byte field had the wrong size.
 * - `decrypt_failure`  : AES-GCM tag mismatch on the outer wrap, or a
 *                        downstream parser rejected the unwrapped
 *                        bytes. Per design, callers surface this as a
 *                        generic "this message could not be decrypted"
 *                        and never leak the discriminator.
 */
export type SecretsErrorKind =
  | "bad_json"
  | "bad_shape"
  | "bad_version"
  | "bad_alg"
  | "bad_base64"
  | "bad_field_length"
  | "decrypt_failure";

export class SecretsError extends Error {
  constructor(
    public readonly kind: SecretsErrorKind,
    message: string,
    cause?: unknown,
  ) {
    super(message, cause === undefined ? undefined : { cause });
    this.name = "SecretsError";
  }
}

// ---------------------------------------------------------------------------
// Public functions
// ---------------------------------------------------------------------------

/**
 * Wrap a serialized inner E2E envelope with the Secrets layer.
 *
 * Pipeline:
 *
 *   1. ECDH(identityPrivateKey, identityPublicKey) -> 32-byte shared
 *      secret. Self-ECDH is a deterministic value — every Secrets
 *      message from the same Account starts from the same IKM.
 *   2. Generate 32 fresh CSPRNG bytes as the per-message HKDF salt.
 *   3. HKDF-SHA256 with info = `"link.secrets.v1" || channelId ||
 *      messageId` (UTF-8) to derive the 32-byte AES key SK_msg.
 *   4. Generate a fresh 12-byte AES-GCM nonce.
 *   5. AES-256-GCM-encrypt the serialized inner E2E envelope under
 *      SK_msg, no AAD.
 *   6. Return `{ v, alg, salt, iv, outerCt }`.
 *
 * @throws on Web Crypto failures or malformed inputs.
 */
export async function wrapSecrets(
  input: WrapSecretsInput,
): Promise<SecretsEnvelope> {
  const innerBytes =
    input.inner instanceof Uint8Array
      ? input.inner.slice()
      : serializeE2eEnvelope(input.inner);

  const subtle = getSubtle();

  // Step 1: self-ECDH. Web Crypto rejects keys that aren't tagged for
  // the `ECDH` algorithm; that's precisely the contract this function
  // documents (see `WrapSecretsInput.identityPrivateKey`).
  const sharedSecret = await deriveSelfEcdh(
    subtle,
    input.identityPrivateKey,
    input.identityPublicKey,
  );

  // Step 2: per-message random salt.
  const salt = new Uint8Array(SECRETS_SALT_BYTES);
  crypto.getRandomValues(salt);

  // Step 3: HKDF derives SK_msg.
  const info = buildSecretsHkdfInfo(input.channelId, input.messageId);
  let skKey: CryptoKey;
  try {
    skKey = await deriveSecretsKey(subtle, sharedSecret, salt, info);
  } finally {
    wipe(sharedSecret);
  }

  // Step 4: per-message AES-GCM nonce.
  const iv = new Uint8Array(SECRETS_IV_BYTES);
  crypto.getRandomValues(iv);

  // Step 5: AES-256-GCM the inner bytes.
  const outerCt = new Uint8Array(
    await subtle.encrypt({ name: "AES-GCM", iv }, skKey, innerBytes),
  );

  // Wipe the local copy of the inner bytes if we own it (we always do
  // — either we serialized them ourselves or we sliced the caller's).
  wipe(innerBytes);

  return {
    v: SECRETS_VERSION,
    alg: SECRETS_ALG,
    salt,
    iv,
    outerCt,
  };
}

/**
 * Strip the Secrets layer from an outer envelope, returning the
 * serialized inner E2E envelope bytes.
 *
 * The returned bytes are exactly what
 * {@link serializeE2eEnvelope} would produce on the matching inner
 * envelope; pass them to `parseE2eEnvelope` (or compare directly) to
 * recover the typed shape.
 *
 * @throws {@link SecretsError} with `kind = "decrypt_failure"` if the
 *   outer AES-GCM tag does not verify — caller surfaces a generic
 *   "this message could not be decrypted" toast.
 */
export async function unwrapSecrets(
  input: UnwrapSecretsInput,
): Promise<Uint8Array> {
  const { outer, channelId, messageId } = input;

  // Defensive shape checks. `parseSecretsEnvelope` already enforces
  // these on the wire-bytes path, but a hand-built envelope object
  // could bypass it.
  assertLength("outer.salt", outer.salt, SECRETS_SALT_BYTES);
  assertLength("outer.iv", outer.iv, SECRETS_IV_BYTES);
  if (outer.v !== SECRETS_VERSION) {
    throw new SecretsError(
      "bad_version",
      `unsupported secrets envelope version: ${String(outer.v)}`,
    );
  }
  if (outer.alg !== SECRETS_ALG) {
    throw new SecretsError(
      "bad_alg",
      `unsupported secrets envelope alg: ${String(outer.alg)}`,
    );
  }

  const subtle = getSubtle();

  const sharedSecret = await deriveSelfEcdh(
    subtle,
    input.identityPrivateKey,
    input.identityPublicKey,
  );

  const info = buildSecretsHkdfInfo(channelId, messageId);
  let skKey: CryptoKey;
  try {
    skKey = await deriveSecretsKey(subtle, sharedSecret, outer.salt, info);
  } finally {
    wipe(sharedSecret);
  }

  let innerBytes: Uint8Array;
  try {
    innerBytes = new Uint8Array(
      await subtle.decrypt(
        { name: "AES-GCM", iv: outer.iv },
        skKey,
        outer.outerCt,
      ),
    );
  } catch (cause) {
    throw new SecretsError(
      "decrypt_failure",
      "AES-GCM tag mismatch unwrapping secrets layer",
      cause,
    );
  }

  return innerBytes;
}

// ---------------------------------------------------------------------------
// Wire format: serialize / parse
// ---------------------------------------------------------------------------

/**
 * Serialize a {@link SecretsEnvelope} to its canonical wire bytes
 * (UTF-8 JSON with unpadded base64url byte fields). Deterministic;
 * same logical envelope yields the same byte sequence.
 *
 * @throws {@link SecretsError} (`bad_field_length`) if any
 *   required-fixed byte field has the wrong length.
 */
export function serializeSecretsEnvelope(env: SecretsEnvelope): Uint8Array {
  if (env.v !== SECRETS_VERSION) {
    throw new SecretsError(
      "bad_version",
      `unsupported secrets envelope version: ${env.v}`,
    );
  }
  if (env.alg !== SECRETS_ALG) {
    throw new SecretsError(
      "bad_alg",
      `unsupported secrets envelope alg: ${env.alg}`,
    );
  }
  assertLength("salt", env.salt, SECRETS_SALT_BYTES);
  assertLength("iv", env.iv, SECRETS_IV_BYTES);
  if (env.outerCt.length === 0) {
    throw new SecretsError("bad_field_length", "outerCt is empty");
  }

  // Hand-rolled JSON for deterministic key order and compact output.
  let s = "{";
  for (let i = 0; i < ENVELOPE_KEY_ORDER.length; i++) {
    const key = ENVELOPE_KEY_ORDER[i]!;
    if (i > 0) s += ",";
    s += JSON.stringify(key);
    s += ":";
    switch (key) {
      case "v":
        s += String(env.v);
        break;
      case "alg":
        s += JSON.stringify(env.alg);
        break;
      case "salt":
        s += JSON.stringify(toBase64Url(env.salt));
        break;
      case "iv":
        s += JSON.stringify(toBase64Url(env.iv));
        break;
      case "outerCt":
        s += JSON.stringify(toBase64Url(env.outerCt));
        break;
    }
  }
  s += "}";
  return TEXT_ENCODER.encode(s);
}

/**
 * Parse the canonical wire bytes back into a typed
 * {@link SecretsEnvelope}.
 *
 * On any malformed input the function throws a {@link SecretsError}
 * with a `kind` that distinguishes "not JSON" from "JSON but wrong
 * shape" from "shape OK but a fixed-length field is the wrong length".
 */
export function parseSecretsEnvelope(bytes: Uint8Array): SecretsEnvelope {
  let raw: unknown;
  try {
    raw = JSON.parse(new TextDecoder().decode(bytes));
  } catch (cause) {
    throw new SecretsError(
      "bad_json",
      "secrets envelope bytes are not valid UTF-8 JSON",
      cause,
    );
  }
  if (typeof raw !== "object" || raw === null) {
    throw new SecretsError(
      "bad_shape",
      "secrets envelope JSON root is not an object",
    );
  }
  const obj = raw as Record<string, unknown>;

  if (obj["v"] !== SECRETS_VERSION) {
    throw new SecretsError(
      "bad_version",
      `unsupported secrets envelope version: ${String(obj["v"])}`,
    );
  }
  if (obj["alg"] !== SECRETS_ALG) {
    throw new SecretsError(
      "bad_alg",
      `unsupported secrets envelope alg: ${String(obj["alg"])}`,
    );
  }

  const salt = decodeFixedField("salt", obj["salt"], SECRETS_SALT_BYTES);
  const iv = decodeFixedField("iv", obj["iv"], SECRETS_IV_BYTES);
  const outerCt = decodeVariableField("outerCt", obj["outerCt"]);
  if (outerCt.length === 0) {
    throw new SecretsError("bad_field_length", "outerCt is empty");
  }

  return {
    v: SECRETS_VERSION,
    alg: SECRETS_ALG,
    salt,
    iv,
    outerCt,
  };
}

// ---------------------------------------------------------------------------
// HKDF info construction
// ---------------------------------------------------------------------------

/**
 * Build the HKDF `info` parameter for a Secrets-layer key derivation.
 *
 * Layout: `"link.secrets.v1" || utf8(channelId) || utf8(messageId)`.
 *
 * No length prefixes are inserted between fields. The two ID fields
 * are application-controlled XID strings whose bytes never overlap
 * with the constant prefix, and a single-purpose use of this info
 * inside `wrapSecrets`/`unwrapSecrets` only ever compares two infos
 * built the same way — so length-prefixing would add complexity
 * without security benefit (matches the rationale documented in
 * `e2e/envelope.ts:buildE2eAad`).
 */
export function buildSecretsHkdfInfo(
  channelId: string,
  messageId: string,
): Uint8Array {
  const channelBytes = TEXT_ENCODER.encode(channelId);
  const messageBytes = TEXT_ENCODER.encode(messageId);
  const out = new Uint8Array(
    SECRETS_INFO_PREFIX.length + channelBytes.length + messageBytes.length,
  );
  out.set(SECRETS_INFO_PREFIX, 0);
  out.set(channelBytes, SECRETS_INFO_PREFIX.length);
  out.set(
    messageBytes,
    SECRETS_INFO_PREFIX.length + channelBytes.length,
  );
  return out;
}

// ---------------------------------------------------------------------------
// Convenience: unwrap straight to a typed inner envelope
// ---------------------------------------------------------------------------

/**
 * Strip the Secrets layer and parse the inner bytes back into a
 * typed {@link E2eEnvelope}. Convenience wrapper around
 * {@link unwrapSecrets} + {@link parseE2eEnvelope}.
 *
 * Provided because almost every caller wants the typed envelope
 * (the bytes-only return type of `unwrapSecrets` exists primarily so
 * the storage layer can persist them verbatim if it wants).
 */
export async function unwrapSecretsToE2e(
  input: UnwrapSecretsInput,
): Promise<E2eEnvelope> {
  const innerBytes = await unwrapSecrets(input);
  try {
    return parseE2eEnvelope(innerBytes);
  } catch (cause) {
    // A successful AES-GCM tag means the bytes are authentic; if
    // they don't parse as an E2E envelope, that's a corrupted-layer
    // bug, not user-facing tampering. Surface as decrypt_failure so
    // the UX is consistent.
    throw new SecretsError(
      "decrypt_failure",
      "unwrapped secrets payload did not parse as an E2E envelope",
      cause,
    );
  }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Run ECDH with a given private/public keypair. For self-ECDH the
 * caller passes their own keys; the function does not assume anything
 * about the relationship between the two keys, so the same helper
 * could be reused for cross-Account derivations in future tasks.
 */
async function deriveSelfEcdh(
  subtle: SubtleCrypto,
  privateKey: CryptoKey,
  publicKey: CryptoKey,
): Promise<Uint8Array> {
  const bits = await subtle.deriveBits(
    { name: "ECDH", public: publicKey },
    privateKey,
    ECDH_SHARED_SECRET_BYTES * 8,
  );
  const out = new Uint8Array(bits);
  if (out.length !== ECDH_SHARED_SECRET_BYTES) {
    throw new Error(
      `deriveSelfEcdh: expected ${ECDH_SHARED_SECRET_BYTES}-byte shared secret, got ${out.length}`,
    );
  }
  return out;
}

/**
 * HKDF-SHA256 with the given salt/info, expanded to a 32-byte
 * AES-256-GCM key. The returned `CryptoKey` is non-extractable.
 */
async function deriveSecretsKey(
  subtle: SubtleCrypto,
  sharedSecret: Uint8Array,
  salt: Uint8Array,
  info: Uint8Array,
): Promise<CryptoKey> {
  if (sharedSecret.length !== ECDH_SHARED_SECRET_BYTES) {
    throw new Error(
      `deriveSecretsKey: expected ${ECDH_SHARED_SECRET_BYTES}-byte shared secret, got ${sharedSecret.length}`,
    );
  }
  if (salt.length !== SECRETS_SALT_BYTES) {
    throw new Error(
      `deriveSecretsKey: expected ${SECRETS_SALT_BYTES}-byte salt, got ${salt.length}`,
    );
  }

  // Pass a fresh copy of `sharedSecret` so the caller can wipe the
  // original after this returns.
  const ikm = await subtle.importKey(
    "raw",
    sharedSecret.slice(),
    { name: "HKDF" },
    false,
    ["deriveKey"],
  );

  const key = await subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt, info },
    ikm,
    { name: "AES-GCM", length: SK_BYTES * 8 },
    false,
    ["encrypt", "decrypt"],
  );

  return key;
}

function assertLength(
  name: string,
  value: Uint8Array,
  expected: number,
): void {
  if (value.length !== expected) {
    throw new SecretsError(
      "bad_field_length",
      `${name} must be exactly ${expected} bytes, got ${value.length}`,
    );
  }
}

function decodeFixedField(
  name: string,
  value: unknown,
  expected: number,
): Uint8Array {
  const decoded = decodeVariableField(name, value);
  if (decoded.length !== expected) {
    throw new SecretsError(
      "bad_field_length",
      `${name} must be exactly ${expected} bytes, got ${decoded.length}`,
    );
  }
  return decoded;
}

function decodeVariableField(name: string, value: unknown): Uint8Array {
  if (typeof value !== "string") {
    throw new SecretsError(
      "bad_shape",
      `${name} must be a base64url string, got ${typeof value}`,
    );
  }
  try {
    return fromBase64Url(value);
  } catch (cause) {
    throw new SecretsError(
      "bad_base64",
      `${name} is not valid unpadded base64url`,
      cause,
    );
  }
}

/** Best-effort scrub of a byte buffer; the engine may keep a copy. */
function wipe(buf: Uint8Array): void {
  buf.fill(0);
}

// ---------------------------------------------------------------------------
// Base64url (RFC 4648 §5), unpadded
// ---------------------------------------------------------------------------
//
// Implemented in-line for the same reason the e2e module does: the
// browser bundle target lacks `Buffer`, and `btoa`/`atob` need the
// URL-safe alphabet swap and padding fixup applied separately. Kept
// local rather than re-exported from `e2e/envelope.ts` to avoid
// coupling the two layers' wire formats.

const B64URL_VALID = /^[A-Za-z0-9_-]*$/;

function toBase64Url(bytes: Uint8Array): string {
  // `btoa` takes a "binary string" (each char's code point in
  // [0,255]). Build it via String.fromCharCode in chunks to avoid
  // call-stack limits for large inputs.
  const CHUNK = 0x8000;
  let bin = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, Math.min(i + CHUNK, bytes.length))),
    );
  }
  const std = btoa(bin);
  return std.replace(/=+$/u, "").replace(/\+/g, "-").replace(/\//g, "_");
}

function fromBase64Url(s: string): Uint8Array {
  if (!B64URL_VALID.test(s)) {
    throw new Error("input contains invalid base64url characters");
  }
  const std = s.replace(/-/g, "+").replace(/_/g, "/");
  const remainder = std.length % 4;
  const padded =
    remainder === 0
      ? std
      : remainder === 2
        ? std + "=="
        : remainder === 3
          ? std + "="
          : (() => {
              throw new Error(
                "base64url length must be 0, 2, or 3 mod 4 (got 1)",
              );
            })();
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}

// Exported solely for unit / property tests in task 3.10.
/** @internal */
export const __test_only = {
  toBase64Url,
  fromBase64Url,
  buildSecretsHkdfInfo,
};
